// Load Dependencies
import React from "react";
import Moment from "moment";
import {Components} from "@webtech/webtech-ui-react/build/webtech-ui";
import {Link} from "react-router-dom";

import {checkPermission} from "../../../../../utils/Authorization/authorization";
import {HallServer} from "../../../reducers/inspectingHall.reducer";

import {formatDateText} from "../../../../../utils/utils";

import "./ServerCard.component.scss";

export interface ServerCardProps {
    server: HallServer;
    subtitle: string;
    system_server_version: string;
    secure_connection: boolean;
    type: number | null;
    hall: number | string;
    subsidiary: number | null;
    subsidiaryDescription?: string | null;
    hasLicensesAccess: boolean | true;
    active: boolean;
    toggleActiveServerAction: (...args: any[]) => any;
    toggleServerDebugMode: (...args: any[]) => any;
    toggleServerPerpetualLicense: (...args: any[]) => any;
    editAction: any;
    cloneAction: any;
    productionServer: any;
    uploadConfigurationRemotely: any;
}

function isLicenseReady(data) {
    return data &&
        data.expired == false &&
        data.expiration != undefined &&
        data.expiration != null;
}

function getBadgeSate(data, showDaysLeft) {
    let badgeState = "unlicensed";
    if (data) {
        if (
            data.expired == true &&
            data.expiration != undefined &&
            data.expiration != null
        ) {
            badgeState = "expired";
        } else if (
            isLicenseReady(data) &&
            showDaysLeft != "--" &&
            showDaysLeft <= data.duration / 4
        ) {
            badgeState = "warning";
        } else if (isLicenseReady(data)) {
            badgeState = "licensed";
        }
    }
    return badgeState
}

function getDaysLeft(data) {
    let showDaysLeft;
    if (data) {
        let expirationDate = Moment(data.expiration);
        let todayDate = Moment();

        showDaysLeft = isLicenseReady(data)
            ? expirationDate.diff(todayDate, "days")
            : "--";
    } else {
        showDaysLeft = "--";
    }
    return showDaysLeft
}

function getExpiresOn(data) {
    let showExpiresOn = "--";
    if (data) {
        showExpiresOn =
            isLicenseReady(data) && data.expiration
                ? Moment(data.expiration).format("YYYY-MM-DD")
                : "--";
    }

    return showExpiresOn
}

function isEGamesSupportedVersion(version) {
    if (!version) return false;

    const versionParts = version.split(".").map(part => parseInt(part, 10));
    const major = versionParts[0];
    const minor = versionParts[1];

    return major > 2 || (major === 2 && minor >= 9);
}

function isSecureConnectionSupportedVersion(version) {
    if (!version) return false;

    const versionParts = version.split(".").map(part => parseInt(part, 10));
    const major = versionParts[0];
    const minor = versionParts[1];

    return major > 2 || (major === 2 && minor >= 8);
}

function ServerCard(props: ServerCardProps) {
    let showDaysLeftLicenseWave = getDaysLeft(props.server.license);
    let showExpiresOnLicenseWave = getExpiresOn(props.server.license);
    let badgeLicenseWaveState = getBadgeSate(props.server.license, showDaysLeftLicenseWave);

    let serverHasLicense = props.server.current_data && props.server.current_data.license
    let licenseServer = serverHasLicense ?
        {
            expiration: props.server.current_data.license.end_license,
            expired: props.server.current_data.license.expired,
            duration: props.server.current_data.license.days_left
        }
        : null
    let showDaysLeftLicenseServer = getDaysLeft(licenseServer);
    let showExpiresOnLicenseServer = getExpiresOn(licenseServer);
    let badgeLicenseServerState = getBadgeSate(licenseServer, showDaysLeftLicenseServer);


    const badgeStates = {
        expired: {
            badgeType: "red",
            label: "Expired",
            icon: "icon-exclamation-circle-light"
        },

        unlicensed: {
            badgeType: "yellow",
            label: "Unlicensed",
            icon: "icon-exclamation-circle-light"
        },

        warning: {
            badgeType: "yellow",
            label: "Expiring",
            icon: "icon-exclamation-circle-light"
        },

        licensed: {
            badgeType: "green",
            label: "Licensed",
            icon: "icon-check-circle-light"
        }
    };

    const serverTypes = ["Undefined", "Local", "Global", "Hybrid"];
    const subsidiaryDescription = props.subsidiaryDescription || "";
    const showEGamesInfo =
        ["NTT-PH", "D2R-PH"].includes(subsidiaryDescription) &&
        isEGamesSupportedVersion(props.system_server_version);
    const showSecureConnectionInfo =
        isSecureConnectionSupportedVersion(props.system_server_version);

    return (
        <div className="server-card">
            <div className={`card-body background-debug-off ${
                props.server.debug_mode && "background-debug-anima"
            }`}>
                <div className="server-header">
                    <div>
                        <div className="card-title h2">{props.subtitle}</div>
                    </div>
                    <div className="server-card-action">
                        {props.server.is_online &&
                            props.server.latest_config_id &&
                            checkPermission("upload_configuration_remotely") &&
                            (<button
                                    className="tooltip-icon upload_configuration_remotely"
                                    onClick={() => props.uploadConfigurationRemotely(props.server.id, props.server.config ? props.server.config.auto_session_enabled : '')}>
                                    <span className="tooltip-text"> Update Remotely</span>
                                    <i className="icon-cloud-upload"/>
                                </button>
                            )}
                        {checkPermission("edit_servers") && (
                            <button
                                className="tooltip-icon"
                                onClick={() => props.editAction(props.server)}
                            >
                                <span className="tooltip-text">Edit</span>
                                <i className="icon-pencil-alt-light"></i>
                            </button>
                        )}
                        {checkPermission("create_servers") && (
                            <button
                                className="tooltip-icon"
                                onClick={() => props.cloneAction(props.server)}
                            >
                                <span className="tooltip-text">Clone</span>
                                <i className="icon-clone-light"></i>
                            </button>
                        )}
                    </div>
                </div>
                {<div className="title-status">Wave Status</div>}
                {checkPermission("spec_history") && (
                    <div className="card-line-info inline stretch">
                        <i
                            className={`icon-file-text ${
                                props.server.latest_spec_version == null
                                    ? ""
                                    : props.server.latest_spec_version ==
                                    props.server.version
                                        ? "green"
                                        : "yellow"
                            }`}
                        />

                        <div className="label-tooltip">
                            {props.server.latest_spec_version ==
                            props.server.version &&
                            props.server.latest_spec_version !== null
                                ? "Certified"
                                : "Not Certified"}
                            {props.server.latest_spec_version !== null &&
                                props.server.latest_spec_version !=
                                props.server.version && (
                                    <React.Fragment>
                                        <button>
                                            <i className="icon-question-circle-solid"></i>
                                        </button>
                                        <div className="messages">
                                            <li>
                                                A new version of the
                                                specification file must be
                                                uploaded.
                                            </li>
                                        </div>
                                    </React.Fragment>
                                )}
                        </div>
                    </div>
                )}

                {props.hasLicensesAccess && (
                    <React.Fragment>
                        <div className="card-line-info">
                            <i
                                className={`${badgeStates[badgeLicenseWaveState].icon} ${badgeStates[badgeLicenseWaveState].badgeType}`}
                            />
                            <div className="badge-label"> {badgeStates[badgeLicenseWaveState].label} </div>
                            <div className="tooltip-icon">
                                <i className="icon-stopwatch-light"/>
                                <span>
                                {showDaysLeftLicenseWave !== "--" &&
                                    showDaysLeftLicenseWave >= 0 &&
                                    `${showDaysLeftLicenseWave} days left`}
                                    {showDaysLeftLicenseWave !== "--" &&
                                        showDaysLeftLicenseWave < 0 &&
                                        `${Math.abs(showDaysLeftLicenseWave)} days ago`}
                                    {showDaysLeftLicenseWave == "--" && showDaysLeftLicenseWave}
                            </span>
                                <span className="tooltip-text">License Remaining Days</span>
                            </div>
                        </div>
                        <div className="card-line-info">
                            <div className="tooltip-icon">
                                <i className="icon-calendar-alt-light"/>
                                {showExpiresOnLicenseWave !== "--"
                                    ? formatDateText(showExpiresOnLicenseWave)
                                    : showExpiresOnLicenseWave}
                                <span className="tooltip-text">License End Date</span>
                            </div>
                        </div>
                        <hr/>
                    </React.Fragment>
                )}
                {<div className="title-status">Live Status</div>}
                {props.server.is_online ?
                    (
                        <div className="card-line-info inline">
                            <i className="icon-wifi-solid hall-info-icon green"/>
                            <div className="badge-label">Online</div>
                            <div className="tooltip-icon">
                                <i className="icon-calendar-alt-light"/>
                                {props.server.last_online
                                    ? formatDateText(props.server.last_online)
                                    : "--"}
                                <span className="tooltip-text">Last Updated</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card-line-info inline">
                            <i className="icon-wifi-slash-light hall-info-icon"/>
                            <div className="badge-label">Offline</div>
                            <div className="tooltip-icon">
                                <i className="icon-calendar-alt-light"/>
                                {props.server.last_online
                                    ? formatDateText(props.server.last_online)
                                    : "--"}
                                <span className="tooltip-text">Last Updated</span>
                            </div>
                        </div>
                    )
                }

                {props.hasLicensesAccess && (
                    <React.Fragment>
                        <div className="card-line-info">
                            <i
                                className={`${badgeStates[badgeLicenseServerState].icon} ${badgeStates[badgeLicenseServerState].badgeType}`}
                            />
                            <div className={"badge-label"}>{badgeStates[badgeLicenseServerState].label}</div>
                            <div className="tooltip-icon">
                                <i className={"icon-stopwatch-light"}/>
                                <span> {showDaysLeftLicenseServer !== "--" &&
                                    showDaysLeftLicenseServer >= 0 &&
                                    `${showDaysLeftLicenseServer} days left`}
                                    {showDaysLeftLicenseServer !== "--" &&
                                        showDaysLeftLicenseServer < 0 &&
                                        `${Math.abs(showDaysLeftLicenseServer)} days ago`}
                                    {showDaysLeftLicenseServer == "--" && showDaysLeftLicenseServer}
                                </span>
                                <span className="tooltip-text">License Remaining Days</span>
                            </div>
                        </div>

                        <div className="card-line-info">
                            <div className="tooltip-icon">
                                <i className="icon-calendar-alt-light"/>
                                {showExpiresOnLicenseServer !== "--"
                                    ? formatDateText(showExpiresOnLicenseServer)
                                    : showExpiresOnLicenseServer}
                                <span className="tooltip-text">License End Date</span>
                            </div>
                        </div>
                    </React.Fragment>
                )}

                <hr/>

                <div className="card-line-info inline">
                    <div className="title">Type</div>
                    <div>{props.type ? serverTypes[props.type] : "--"}</div>
                </div>
                <div className="card-line-info inline">
                    <div className="title">S. Server Version</div>
                    <div>
                        {props.system_server_version
                            ? props.system_server_version
                            : "--"}
                    </div>
                </div>
                {showSecureConnectionInfo && (
                    <div className="card-line-info inline">
                        <div className="title">Using Secure Connection</div>
                        <div>
                            {props.secure_connection
                                ? <i className="icon-lock-solid green"/>
                                : <i className="icon-lock-solid red"/>}
                        </div>
                    </div>
                )}
                {showEGamesInfo && (
                    <div className="card-line-info inline">
                        <div className="title">eGames</div>
                        <div>
                            <i
                                className={
                                    props.server.is_egames
                                        ? "icon-check-circle-light green"
                                        : "icon-times-circle-light red"
                                }
                            />
                        </div>
                    </div>
                )}
            </div>
            {parseInt(props.server.system_server_version.substring(0, props.server.system_server_version.indexOf('.'))) >= 2 && !props.productionServer && checkPermission("toggle_servers_debug_mode") &&
                (<div className="card-footer-debug">
                    <div className="status">
                        <span>Debug Mode </span>
                        <Components.Switch
                            onClick={() =>
                                props.toggleServerDebugMode(props.server.id)
                            }
                            on={props.server.debug_mode}
                            enabled
                        />
                    </div>
                </div>)
            }
            {(props.server.system_server_version == "1.9.51" ||
                    ((parseInt(props.server.system_server_version.substring(0, props.server.system_server_version.indexOf('.'))) >= 2 &&
                        parseInt(props.server.system_server_version.substring(props.server.system_server_version.indexOf('.') + 1, props.server.system_server_version.lastIndexOf('.'))) >= 1))) &&
                [9, 10, 12, 13].includes(props.subsidiary) && checkPermission("toggle_servers_perpetual_license") &&
                (<div className="card-footer">
                    <div className="status">
                        <span>Perpetual license</span>
                        <Components.Switch
                            onClick={() =>
                                props.toggleServerPerpetualLicense(props.server.id)
                            }
                            on={props.server.perpetual_license}
                            enabled
                        />
                    </div>
                </div>)
            }
            <div className="card-footer">
                {(checkPermission("licensing_history") ||
                    checkPermission("upload_seed") ||
                    checkPermission("download_license") ||
                    checkPermission("manage_licensing") ||
                    checkPermission("spec_history") ||
                    checkPermission("spec_update") ||
                    checkPermission("list_configurations") ||
                    checkPermission("view_license")) && (
                    <Link
                        to={`/halls/${props.hall}/servers/${props.server.id}`}
                    >
                        Details
                    </Link>
                )}
                {checkPermission("toggle_servers") && (
                    <div className="status">
                        <span>{props.active ? "Active" : "Inactive"}</span>
                        <Components.Switch
                            onClick={() =>
                                props.toggleActiveServerAction(props.server.id)
                            }
                            on={props.active}
                            enabled
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default ServerCard;
